<script setup>
import axios from 'axios';
import { BButton, BLink, BTable } from 'bootstrap-vue-next';
import { computed, onMounted, ref } from 'vue';

import { SUBMISSION_KIND_LABELS, SUBMISSION_REVIEW_ACTIONS } from '~/common/Constants';
import { toLocaleDateStringIgnoringTimezone } from '~/common/DateUtils';
import { dateFieldFormatter } from '~/common/FieldFormats';
import { eventSummary } from '~/common/SubmissionDetails';
import { useSubmissionsRefresh } from '~/common/useSubmissionsRefresh';

import ErrorState from './ErrorState.vue';
import SubmissionReviewModal from './SubmissionReviewModal.vue';

const { notify } = useSubmissionsRefresh();

const props = defineProps({
  showEmptyState: {
    type: Boolean,
    default: false,
  },
  showHeading: {
    type: Boolean,
    default: false,
  },
});

const submissions = ref([]);
const loading = ref(true);
const error = ref(null);
const busy = ref(false);

const showReviewModal = ref(false);
const reviewAction = ref(SUBMISSION_REVIEW_ACTIONS.approve);
const reviewTarget = ref(null);
const reviewError = ref(null);

const shouldRender = computed(() =>
  props.showEmptyState || loading.value || error.value != null || submissions.value.length > 0,
);

const fields = [
  { key: 'username', label: 'User' },
  { key: 'kind', label: 'Type', formatter: ({ value }) => SUBMISSION_KIND_LABELS[value] || value },
  { key: 'name', label: 'Name' },
  { key: 'details', label: 'Details' },
  { key: 'occurred_at', label: 'Date', formatter: ({ value }) => toLocaleDateStringIgnoringTimezone(value) },
  { key: 'created_at', label: 'Submitted', formatter: dateFieldFormatter },
  { key: 'actions', label: '' },
];

const fetchData = async () => {
  loading.value = true;
  error.value = null;
  try {
    const { data } = await axios.get('/api/v1/submissions');
    submissions.value = data;
  } catch (e) {
    error.value = e;
  } finally {
    loading.value = false;
  }
};

const openReview = (submission, action) => {
  reviewTarget.value = submission;
  reviewAction.value = action;
  reviewError.value = null;
  showReviewModal.value = true;
};

const processSubmission = async (action, payload = {}) => {
  if (busy.value) {
    return;
  }

  const submission = reviewTarget.value;
  busy.value = true;
  reviewError.value = null;
  try {
    await axios.post(`/api/v1/submissions/${submission.id}/${action}`, payload);
    submissions.value = submissions.value.filter(s => s.id !== submission.id);
    showReviewModal.value = false;
    notify();
  } catch {
    reviewError.value = `Failed to ${action} this submission. Please try again.`;
  } finally {
    busy.value = false;
  }
};

const approve = () => processSubmission('approve');

const reject = reason => processSubmission('reject', { reason });

onMounted(fetchData);
</script>

<template>
  <div
    v-if="shouldRender"
    class="mb-4"
  >
    <h2 v-if="showHeading">Pending submissions</h2>
    <ErrorState v-if="error" />
    <BTable
      v-else
      :items="submissions"
      :fields="fields"
      :busy="loading"
      :show-empty="showEmptyState"
      empty-text="There are no submissions waiting for review."
      small
      hover
      responsive
    >
      <template #cell(username)="{ value }">
        <BLink :to="{ name: 'user', params: { userHandle: value } }">
          {{ value }}
        </BLink>
      </template>
      <template #cell(name)="{ item }">
        <BLink
          v-if="item.link"
          is-unsafe-link
          :href="item.link"
          target="_blank"
        >
          {{ item.name }}
        </BLink>
        <span v-else>{{ item.name }}</span>
      </template>
      <template #cell(details)="{ item }">
        <span class="text-body-secondary">{{ eventSummary(item) }}</span>
      </template>
      <template #cell(actions)="{ item }">
        <BButton
          size="sm"
          variant="outline-success"
          class="me-1"
          @click="openReview(item, SUBMISSION_REVIEW_ACTIONS.approve)"
        >
          Approve
        </BButton>
        <BButton
          size="sm"
          variant="outline-danger"
          @click="openReview(item, SUBMISSION_REVIEW_ACTIONS.reject)"
        >
          Reject
        </BButton>
      </template>
    </BTable>

    <SubmissionReviewModal
      v-if="reviewTarget"
      v-model="showReviewModal"
      :action="reviewAction"
      :submission="reviewTarget"
      :busy="busy"
      :error="reviewError"
      @approve="approve"
      @reject="reject"
    />
  </div>
</template>
